/* ==========================================================================
   ff-bus.js
   Shared runtime for the ff-* landing sections.

   Responsibilities
   - A tiny namespaced event bus so sections that cannot see each other in the
     DOM (hero vs. sticky bar) stay in sync regardless of section order.
   - A base custom element that is safe to upgrade repeatedly, which is what
     the Theme Editor does on every section re-render.
   - Theme Editor lifecycle plumbing.

   Loaded with `defer`. No dependencies.
   ========================================================================== */
(function () {
  'use strict';

  if (window.FFBus) return; // idempotent — safe if the asset is included twice

  var EVENTS = {
    variantChange: 'ff:variant-change',
    sellingPlanChange: 'ff:selling-plan-change',
    purchaseStateChange: 'ff:purchase-state-change',
    cartAddSuccess: 'ff:cart-add-success',
    cartAddError: 'ff:cart-add-error'
  };

  /**
   * Publish a namespaced CustomEvent on `document`.
   * Every payload carries productId + sourceSectionId so listeners can ignore
   * traffic for a different product or their own echo.
   */
  function emit(name, detail) {
    document.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        cancelable: false,
        detail: detail || {}
      })
    );
  }

  function on(name, handler) {
    document.addEventListener(name, handler);
    return function off() {
      document.removeEventListener(name, handler);
    };
  }

  /** Format money using the shop's active format, never string concatenation. */
  function formatMoney(cents, format) {
    if (cents == null || isNaN(cents)) return '';
    var fmt = format || (window.FFBus && window.FFBus.moneyFormat) || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function withCommas(value, precision, thousands, decimal) {
      if (isNaN(value) || value == null) return '0';
      var amount = (value / 100).toFixed(precision);
      var parts = amount.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var match = fmt.match(placeholder);
    if (!match) return fmt;

    var value;
    switch (match[1]) {
      case 'amount':
        value = withCommas(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = withCommas(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withCommas(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withCommas(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = withCommas(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withCommas(cents, 0, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = withCommas(cents, 2, ' ', '.');
        break;
      default:
        value = withCommas(cents, 2, ',', '.');
    }
    return fmt.replace(placeholder, value);
  }

  /** Read a <script type="application/json"> child safely. */
  function readJSON(root, selector) {
    try {
      var el = root.querySelector(selector);
      if (!el) return null;
      return JSON.parse(el.textContent);
    } catch (error) {
      console.warn('[ff] Could not parse JSON for', selector, error);
      return null;
    }
  }

  /**
   * Base element. Subclasses implement `init()` and optionally `destroy()`.
   * `init()` is guaranteed to run at most once per DOM connection, which keeps
   * listeners from stacking up after repeated Theme Editor reloads.
   */
  var FFElement = (function () {
    function Base() {
      return Reflect.construct(HTMLElement, [], this.constructor);
    }
    Base.prototype = Object.create(HTMLElement.prototype);
    Base.prototype.constructor = Base;
    Object.setPrototypeOf(Base, HTMLElement);

    Base.prototype.connectedCallback = function () {
      if (this.__ffReady) return;
      this.__ffReady = true;
      this.__ffCleanups = [];
      try {
        if (typeof this.init === 'function') this.init();
      } catch (error) {
        console.error('[ff] init failed for', this.tagName.toLowerCase(), error);
      }
    };

    Base.prototype.disconnectedCallback = function () {
      this.__ffReady = false;
      (this.__ffCleanups || []).forEach(function (fn) {
        try {
          fn();
        } catch (e) {
          /* noop */
        }
      });
      this.__ffCleanups = [];
      try {
        if (typeof this.destroy === 'function') this.destroy();
      } catch (error) {
        console.error('[ff] destroy failed', error);
      }
    };

    /** Register a listener that is torn down automatically on disconnect. */
    Base.prototype.listen = function (target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.__ffCleanups.push(function () {
        target.removeEventListener(type, handler, options);
      });
    };

    /** Register an arbitrary teardown callback (observers, timers, …). */
    Base.prototype.cleanup = function (fn) {
      this.__ffCleanups.push(fn);
    };

    Base.prototype.sectionId = function () {
      return this.getAttribute('data-section-id') || '';
    };

    Base.prototype.productId = function () {
      var id = this.getAttribute('data-product-id');
      return id ? String(id) : '';
    };

    return Base;
  })();

  /** Define a custom element only once, even if the asset loads twice. */
  function define(name, ctor) {
    var registry = window.customElements;
    if (!registry || registry.get(name)) return;
    registry.define(name, ctor);
  }

  /**
   * Theme Editor lifecycle. Custom elements re-upgrade themselves when Shopify
   * swaps section HTML, so we only need to forward block select/deselect so
   * sections can reveal the block a merchant is editing.
   */
  function bindDesignMode() {
    if (!window.Shopify || !window.Shopify.designMode) return;

    ['shopify:block:select', 'shopify:block:deselect'].forEach(function (type) {
      document.addEventListener(type, function (event) {
        var host = event.target.closest('[data-ff-reveals-blocks]');
        if (host && typeof host.onBlockSelect === 'function') {
          host.onBlockSelect(event.target, type === 'shopify:block:select');
        }
      });
    });

    document.addEventListener('shopify:section:load', function (event) {
      // Elements upgrade on their own; re-broadcast so peers can resync.
      var el = event.target.querySelector('[data-ff-broadcasts]');
      if (el && typeof el.broadcast === 'function') el.broadcast();
    });

    document.addEventListener('shopify:section:unload', function (event) {
      emit('ff:section-unload', { sectionId: event.detail.sectionId });
    });

    document.addEventListener('shopify:section:reorder', function () {
      document.querySelectorAll('[data-ff-broadcasts]').forEach(function (el) {
        if (typeof el.broadcast === 'function') el.broadcast();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindDesignMode);
  } else {
    bindDesignMode();
  }

  window.FFBus = {
    EVENTS: EVENTS,
    emit: emit,
    on: on,
    define: define,
    Element: FFElement,
    formatMoney: formatMoney,
    readJSON: readJSON,
    prefersReducedMotion: function () {
      return (
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      );
    }
  };
})();
